import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { cn } from "@/lib/utils"

/* -------------------------------------------------------------------------- */
/*                                  geometry                                  */
/* -------------------------------------------------------------------------- */

const size = (value) => (typeof value === "number" ? `${value}px` : value)

const clamp = (value, low, high) => Math.max(low, Math.min(high, value))

/**
 * The row is four columns and a tail of slats, and it is a strip that slides
 * rather than a ring that turns.
 *
 * Four columns share out whatever is left once the open card, the slats and the
 * gaps are paid for. The open card starts from a 16:9 block and then gives a
 * little back — hence the negative first share. Column −1 and anything past
 * column 3 is a slat, so a card leaving the front simply narrows to a slat and
 * carries on out of the left edge.
 */
const SHARES = [-0.06, 0.61, 0.3, 0.15]
/** The hovered column takes more room. */
const STRETCHED = [0, 0.71, 0.4, 0.25]
/** Its neighbours give a little up to pay for it. */
const SQUEEZED = [-0.12, 0.59, 0.28, 0.13]

/* -------------------------------------------------------------------------- */
/*                                    hooks                                   */
/* -------------------------------------------------------------------------- */

/** True while the reader asks for less movement. */
function useReducedMotion() {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const read = () => setReduced(query.matches)
    read()
    query.addEventListener("change", read)
    return () => query.removeEventListener("change", read)
  }, [])

  return reduced
}

/* -------------------------------------------------------------------------- */
/*                                 component                                  */
/* -------------------------------------------------------------------------- */

/**
 * A carousel that gives one panel the room and squeezes the rest into slats
 * down the right-hand side. Opening a slat widens it and slides the row along;
 * the copy and the button underneath cross-fade to match.
 *
 * `gsap`-free: the original assumed 5+ slides (always at least 1 slat, wrapping
 * the slide index to fill the window) — with fewer than 4 slides that wrap
 * would repeat a real slide as a "different" slat. Fixed here by letting slats
 * drop to 0 and capping the visible window at the actual slide count, so a
 * short list (e.g. 2 real projects) never shows a duplicate.
 */
export function SqueezeCarousel({
  slides,
  defaultIndex = 0,
  onIndexChange,
  height = "clamp(180px, 32cqi, 340px)",
  slatWidth = 8,
  slatGap = 8,
  gap = 16,
  radius = 6,
  duration = 1000,
  hoverGrow = true,
  autoplay = false,
  interval = 6000,
  controls = true,
  accent = "var(--sq-accent, var(--primary, currentColor))",
  accentForeground = "var(--sq-accent-foreground, var(--primary-foreground, white))",
  label = "Featured",
  panelClassName,
  className,
  style,
  ...props
}) {
  const count = slides.length
  const wrap = (i) => ((i % count) + count) % count

  // Four columns plus a tail of slats. Fewer than 4 slides: no slats, no
  // wraparound padding — just show what's real.
  const slats = clamp(count - 4, 0, 3)
  const visible = Math.min(4 + slats, count)

  const reduced = useReducedMotion()
  const ms = reduced ? 0 : duration

  const ids = useId()
  // Seeded past the initial window so keys handed out later never collide
  // with the ones below — reading/mutating a ref during render (even inside
  // a useState lazy initializer) isn't pure, so the initial keys are plain
  // indices instead of a counter.
  const seed = useRef(visible)
  const strip = useRef(null)

  /* --- the strip -------------------------------------------------------- */

  const window0 = () =>
    Array.from({ length: visible }, (_, p) => ({
      key: p,
      slide: wrap(defaultIndex + p),
    }))

  const [cards, setCards] = useState(window0)
  const [column, setColumn] = useState(0)
  const columnRef = useRef(0)
  const forward = useRef(true)
  const [slid, setSlid] = useState(0)
  const [still, setStill] = useState(false)
  const [hover, setHover] = useState(-1)

  const open = cards[-column]?.slide ?? defaultIndex
  const timers = useRef([])

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  const settle = useCallback(() => {
    setCards((strip) =>
      forward.current ? strip.slice(-visible) : strip.slice(0, visible)
    )
    columnRef.current = 0
    setColumn(0)
    setSlid(0)
    setStill(true)
  }, [visible])

  useLayoutEffect(() => {
    if (!still) return
    const id = requestAnimationFrame(() => setStill(false))
    return () => cancelAnimationFrame(id)
  }, [still])

  const step = useCallback(
    (by) => {
      if (count < 2 || by === 0) return

      timers.current.forEach(clearTimeout)
      timers.current = []
      forward.current = by > 0

      if (by > 0) {
        setCards((strip) => [
          ...strip,
          ...Array.from({ length: by }, (_, k) => ({
            key: seed.current++,
            slide: wrap(strip[strip.length - 1].slide + 1 + k),
          })),
        ])
        columnRef.current -= by
        setColumn(columnRef.current)
        setSlid((s) => s - by)
      } else {
        setCards((strip) => [
          ...Array.from({ length: -by }, (_, k) => ({
            key: seed.current++,
            slide: wrap(strip[0].slide - (-by - k)),
          })),
          ...strip,
        ])
        setSlid((s) => s + by)
        setStill(true)
        timers.current.push(window.setTimeout(() => setSlid(0), 0))
      }

      timers.current.push(window.setTimeout(settle, ms + 20))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, ms, settle]
  )

  useEffect(() => {
    onIndexChange?.(open)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /* --- autoplay --------------------------------------------------------- */

  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (!autoplay || paused || reduced || count < 2) return
    const timer = window.setTimeout(() => step(1), interval)
    return () => clearTimeout(timer)
  }, [autoplay, paused, reduced, count, open, interval, step])

  /* --- keyboard --------------------------------------------------------- */

  const onKeyDown = (event) => {
    const moves = { ArrowRight: 1, ArrowLeft: -1 }
    const by = moves[event.key]
    if (by === undefined) return
    event.preventDefault()
    step(by)
  }

  if (!count) return null

  /* --- render ----------------------------------------------------------- */

  const slat = size(slatWidth)
  const shares = hoverGrow && hover >= 0 && hover <= 3 && !reduced ? null : SHARES

  const shareOf = (col) => {
    if (shares) return SHARES[col]
    return hover === col ? STRETCHED[col] : SQUEEZED[col]
  }

  const widthOf = (col) => {
    if (col < 0 || col > 3) return slat
    if (col === 0) return `calc(var(--sq-hero) + var(--sq-room) * ${shareOf(0)})`
    return `calc(var(--sq-room) * ${shareOf(col)})`
  }

  const vars = {
    "--sq-h": size(height),
    "--sq-gap": size(gap),
    "--sq-slat-gap": size(slatGap),
    "--sq-radius": size(radius),
    "--sq-ms": `${ms}ms`,
    "--sq-ease": "cubic-bezier(0.16, 1, 0.3, 1)",
    "--sq-fill": accent,
    "--sq-on-fill": accentForeground,
    "--sq-hero": "calc(var(--sq-h) * 16 / 9)",
    "--sq-room": `calc(100cqi - var(--sq-hero) - ${slats} * var(--sq-slat-gap) - 3 * var(--sq-gap) - ${slats} * ${slat})`,
  }

  const move = `translateX(calc(${slid} * (${slat} + var(--sq-gap))))`

  return (
    <div
      className={cn("flex w-full flex-col", className)}
      style={{
        containerType: "inline-size",
        ...vars,
        ...style,
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        setPaused(false)
        setHover(-1)
      }}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      {...props}
    >
      {controls && count > 1 && (
        <div className="flex justify-end gap-2" style={{ marginBottom: 16 }}>
          <Arrow back label="Previous" onClick={() => step(-1)} />
          <Arrow label="Next" onClick={() => step(1)} />
        </div>
      )}

      <div className="w-full overflow-hidden" style={{ height: "var(--sq-h)" }}>
        <div
          ref={strip}
          role="tablist"
          aria-label={label}
          aria-orientation="horizontal"
          onKeyDown={onKeyDown}
          className="flex h-full w-max"
          style={{
            transform: move,
            transition: still ? "none" : `transform var(--sq-ms) var(--sq-ease)`,
          }}
        >
          {cards.map((card, place) => {
            const col = place + column
            const slide = slides[card.slide]
            const front = col === 0

            return (
              <button
                key={card.key}
                type="button"
                role="tab"
                id={`${ids}-tab-${card.key}`}
                aria-selected={front}
                aria-controls={`${ids}-panel`}
                aria-label={slide.title}
                tabIndex={front ? 0 : -1}
                onMouseMove={() => hoverGrow && setHover(col)}
                onClick={() => col > 0 && step(col)}
                className={cn(
                  "relative isolate h-full shrink-0 cursor-pointer overflow-hidden bg-muted p-0",
                  "outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  "focus-visible:ring-[var(--sq-fill)] focus-visible:ring-offset-background",
                  panelClassName
                )}
                style={{
                  width: widthOf(col),
                  marginLeft: place === 0 ? 0 : col < 4 ? "var(--sq-gap)" : "var(--sq-slat-gap)",
                  borderRadius: `min(var(--sq-radius), calc(${widthOf(col)} / 2))`,
                  transitionProperty: "width, margin-left",
                  transitionDuration: still ? "0s" : "var(--sq-ms)",
                  transitionTimingFunction: "var(--sq-ease)",
                }}
              >
                <Picture slide={slide} />

                {slide.overlay && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end"
                    style={{
                      padding: "64px 16px 16px",
                      opacity: front ? 1 : 0,
                      transition: `opacity var(--sq-ms) var(--sq-ease)`,
                      backgroundImage: "linear-gradient(to top, rgb(0 0 0 / 0.55), transparent)",
                    }}
                  >
                    {slide.overlay}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div id={`${ids}-panel`} role="tabpanel" aria-live="polite" className="grid" style={{ marginTop: 24 }}>
        {slides.map((slide, i) => {
          const shown = i === open

          return (
            <div
              key={slide.id ?? i}
              aria-hidden={!shown}
              className={cn(
                "col-start-1 row-start-1 flex flex-col gap-4",
                "@xl:flex-row @xl:items-start @xl:justify-between @xl:gap-10"
              )}
              style={{
                opacity: shown ? 1 : 0,
                visibility: shown ? "visible" : "hidden",
                pointerEvents: shown ? "auto" : "none",
                transition: `opacity var(--sq-ms) var(--sq-ease), visibility var(--sq-ms)`,
              }}
            >
              <p className="max-w-[46rem] text-[15px] leading-[1.6] text-balance @lg:text-[17px]">
                <span className="text-foreground">{slide.title}</span>{" "}
                {slide.description && <span className="text-muted-foreground">{slide.description}</span>}
              </p>

              {slide.action && <Action slide={slide} shown={shown} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                   pieces                                   */
/* -------------------------------------------------------------------------- */

/**
 * Drawn at a fixed 16:9 block and centred, never at the width of its card.
 * Left to itself `object-fit: cover` reads whichever edge binds — height while
 * the card is a slat, width once it opens — so the picture would rescale
 * mid-slide and be resampled every frame. One block means one scale: the card
 * only ever changes how much of it you can see.
 */
function Picture({ slide }) {
  const box = {
    width: "var(--sq-hero)",
    minWidth: "100%",
  }

  if (slide.image) {
    return (
      <img
        src={slide.image}
        srcSet={slide.srcSet}
        sizes={slide.srcSet ? "(max-width: 768px) 92vw, 900px" : undefined}
        alt={slide.imageAlt ?? ""}
        draggable={false}
        loading="lazy"
        className="absolute inset-y-0 left-1/2 h-full max-w-none -translate-x-1/2 object-cover"
        style={box}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      className="absolute inset-y-0 left-1/2 -translate-x-1/2"
      style={{ background: slide.background, ...box }}
    />
  )
}

function Arrow({ back = false, label, onClick }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "grid size-9 cursor-pointer place-items-center rounded-md",
        "bg-[var(--sq-fill)] text-[var(--sq-on-fill)]",
        "transition-opacity hover:opacity-85 outline-none",
        "focus-visible:ring-2 focus-visible:ring-[var(--sq-fill)]",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      )}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path
          d={
            back
              ? "M9.6 2.6 5.1 7.1h9.1v1.8H5.1l4.5 4.5-1.2 1.2-6-6L1.8 8l.6-.6 6-6 1.2 1.2Z"
              : "M6.4 2.6l4.5 4.5H1.8v1.8h9.1l-4.5 4.5 1.2 1.2 6-6 .6-.6-.6-.6-6-6-1.2 1.2Z"
          }
        />
      </svg>
    </button>
  )
}

/** The button under the copy. A link when it has an `href`, a button otherwise. */
function Action({ slide, shown }) {
  const inside = (
    <>
      {slide.action}
      <svg
        width="6"
        height="9"
        viewBox="0 0 6 9"
        fill="none"
        aria-hidden="true"
        className="transition-transform duration-200 group-hover/sq-action:translate-x-0.5"
      >
        <path
          d="M1.2 1 4.7 4.5 1.2 8"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </>
  )

  const dress = cn(
    "group/sq-action inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md",
    "bg-[var(--sq-fill)] text-sm font-medium text-[var(--sq-on-fill)]",
    "transition-opacity hover:opacity-85 outline-none",
    "focus-visible:ring-2 focus-visible:ring-[var(--sq-fill)]",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-background"
  )
  const dressStyle = { padding: "10px 16px" }

  if (slide.href) {
    return (
      <a
        href={slide.href}
        target={slide.target}
        rel={slide.target === "_blank" ? "noreferrer" : undefined}
        tabIndex={shown ? 0 : -1}
        onClick={slide.onAction}
        className={dress}
        style={dressStyle}
      >
        {inside}
      </a>
    )
  }

  return (
    <button type="button" tabIndex={shown ? 0 : -1} onClick={slide.onAction} className={dress} style={dressStyle}>
      {inside}
    </button>
  )
}

export default SqueezeCarousel
